import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DragStartEvent, UniqueIdentifier } from "@dnd-kit/core";
import {
    announceCancel,
    announcePickUp,
    announceRefusedSlot,
    announceSkippedPlace,
    announceSlot,
    announceSortedSlot,
} from "./boardAnnouncements";
import {
    getRenderedCells,
    getSortHintCellKey,
    getStateAfterDrop,
    getTargetRefusal,
    NO_LANDING_KEYS,
    resolveDragging,
    retarget,
    startDrag,
    type BoardDraggingState,
    type BoardDragState,
} from "./boardDragState";
import {
    announceGroupCancel,
    announceGroupPickUp,
} from "./boardGroupAnnouncements";
import type { BoardMoveTo } from "./boardMove";
import { NO_REFUSALS } from "./boardMoveRules";
import {
    DEFAULT_BOARD_MOTION_TOKENS,
    getLandingDurationMs,
    readBoardMotionTokens,
    useIsReducedMotion,
} from "./boardMotion";
import { getHeldPlaceRefusal, isPlacingInSort } from "./boardPlaceSpot";
import { useBoardDragFocus } from "./useBoardDragFocus";
import { useBoardDragStore, type UseBoardDragArgs } from "./useBoardDragStore";
import { useBoardDrop } from "./useBoardDrop";
import { useBoardKeyboardStep } from "./useBoardKeyboardStep";
import { useBoardPointerHover } from "./useBoardPointerHover";

// Fallback for drops dnd-kit doesn't animate (nothing to travel).
const SETTLE_GRACE_MS = 100;

/**
 * Drag state for `Board`. While dragging it renders a preview (the dragged
 * copy at its target) and reports nothing; the drop fires one `onMove`.
 * Pointer drops keep the preview until the overlay has landed on its slot.
 */
export const useBoardDrag = <TItem>(args: UseBoardDragArgs<TItem>) => {
    const {
        state,
        stateRef,
        latestRef,
        model,
        commit,
        announce,
        announceDrag,
        announceRefusal,
        announceKeyboardEdge,
        announceSortedCell,
        announceLockedCard,
        getGroup,
        getRefusals,
    } = useBoardDragStore(args);
    const drop = useBoardDrop({
        latestRef,
        announce,
        announceRefusal,
        announceMessage: args.announceMessage,
    });
    const [motionTokens, setMotionTokens] = useState(
        DEFAULT_BOARD_MOTION_TOKENS,
    );
    const isReducedMotion = useIsReducedMotion();
    const motion = useMemo(
        () => ({ ...motionTokens, isReduced: isReducedMotion }),
        [motionTokens, isReducedMotion],
    );
    const settleTimeoutRef = useRef<number | undefined>(undefined);
    useEffect(() => () => window.clearTimeout(settleTimeoutRef.current), []);

    const moveTo = useCallback(
        (
            current: BoardDraggingState,
            { target, skippedRefusal }: BoardMoveTo,
        ) => {
            const next = retarget(latestRef.current, current, target);
            if (next === null) {
                return;
            }
            commit(next);
            const refusal = getTargetRefusal(next);
            if (refusal === undefined) {
                const placeRefusal = getHeldPlaceRefusal(
                    latestRef.current,
                    next,
                );
                const toSlotMessage =
                    placeRefusal === undefined
                        ? announceSlot
                        : announceSortedSlot(placeRefusal);
                announce(
                    resolveDragging(latestRef.current, next).cells,
                    next.activeKey,
                    skippedRefusal === undefined ||
                        skippedRefusal === placeRefusal
                        ? toSlotMessage
                        : announceSkippedPlace(toSlotMessage, skippedRefusal),
                );
            } else {
                announceRefusal({
                    state: next,
                    reason: refusal,
                    toMessage:
                        skippedRefusal === undefined ||
                        skippedRefusal === refusal
                            ? announceRefusedSlot
                            : announceSkippedPlace(
                                  announceRefusedSlot,
                                  skippedRefusal,
                              ),
                });
            }
        },
        [announce, announceRefusal, commit, latestRef],
    );

    const settle = useCallback(
        (activeId: UniqueIdentifier) => {
            const current = stateRef.current;
            if (
                current.phase === "settling" &&
                current.activeKey === activeId
            ) {
                commit({
                    phase: "idle",
                    focusKey: current.focusKey,
                    isKeyboard: false,
                });
            }
        },
        [commit, stateRef],
    );

    const isSettling = useCallback(
        () => stateRef.current.phase === "settling",
        [stateRef],
    );

    const finish = useCallback(
        (current: BoardDraggingState, next: BoardDragState<TItem>) => {
            commit(next);
            if (next.phase === "settling") {
                window.clearTimeout(settleTimeoutRef.current);
                settleTimeoutRef.current = window.setTimeout(
                    () => settle(current.activeKey),
                    getLandingDurationMs(motion) + SETTLE_GRACE_MS,
                );
            }
        },
        [commit, motion, settle],
    );

    const onDragStart = useCallback(
        ({ active, activatorEvent }: DragStartEvent) => {
            const latestCells = latestRef.current.cells;
            const isKeyboard = activatorEvent instanceof KeyboardEvent;
            const next = startDrag({
                cells: latestCells,
                activeKey: String(active.id),
                isKeyboard,
                getGroup,
                getRefusals,
            });
            if (next === null) {
                return;
            }
            setMotionTokens(readBoardMotionTokens());
            commit(next);
            announceDrag({
                cells: latestCells,
                state: next,
                toMessage: announcePickUp,
                toGroupMessage: announceGroupPickUp,
            });
        },
        [announceDrag, commit, getGroup, getRefusals, latestRef],
    );

    const isPointerDrag = state.phase === "dragging" && !state.isKeyboard;
    const onDragHover = useBoardPointerHover({
        stateRef,
        latestRef,
        moveTo,
        commit,
        isPointerDrag,
    });

    const onDragEnd = useCallback(() => {
        const current = stateRef.current;
        if (current.phase === "dragging") {
            finish(current, getStateAfterDrop(current, drop(current)));
        }
    }, [drop, finish, stateRef]);

    const onDragCancel = useCallback(() => {
        const current = stateRef.current;
        if (current.phase !== "dragging") {
            return;
        }
        const originalCells = latestRef.current.cells;
        announceDrag({
            cells: originalCells,
            state: current,
            toMessage: announceCancel,
            toGroupMessage: announceGroupCancel,
        });
        finish(
            current,
            getStateAfterDrop(current, {
                cells: originalCells,
                focusKey: current.activeKey,
                landingKeys: NO_LANDING_KEYS,
            }),
        );
    }, [announceDrag, finish, latestRef, stateRef]);

    /** Drops a drag dnd-kit ended without calling `onDragEnd`; see `BoardOrphanedDragGuard`. */
    const releaseOrphanedDrag = useCallback(() => {
        const current = stateRef.current;
        if (current.phase === "dragging") {
            commit({
                phase: "idle",
                focusKey: current.activeKey,
                isKeyboard: false,
            });
        }
    }, [commit, stateRef]);

    const stepKeyboard = useBoardKeyboardStep({
        stateRef,
        latestRef,
        commit,
        moveTo,
        announceKeyboardEdge,
        announceSortedCell,
        announceRefusal,
    });

    const registerActivator = useBoardDragFocus(state);

    const renderedCells = useMemo(
        () => getRenderedCells(model, state),
        [model, state],
    );

    return {
        motion,
        renderedCells,
        activeKey: state.phase === "idle" ? null : state.activeKey,
        /** The card focus returns to after a drop. */
        focusKey: state.phase === "dragging" ? null : state.focusKey,
        isKeyboardDrag: state.phase === "dragging" && state.isKeyboard,
        isPointerDrag,
        /** The place key is held over a sorted board: its hint steps aside. */
        isPlacingInSort:
            state.phase === "dragging" && isPlacingInSort(model, state),
        /** Why `canPlaceAt` refuses the held drag's slot: the hint says it instead. */
        placeRefusal:
            state.phase === "dragging"
                ? getHeldPlaceRefusal(model, state)
                : undefined,
        isSlotInstant: state.phase === "dragging" && state.isSlotInstant,
        refusals: state.phase === "dragging" ? state.refusals : NO_REFUSALS,
        /** The selection the drag carries, the grabbed item included. */
        group: state.phase === "dragging" ? state.group : null,
        /** The other cards a pointer group drop moved, while it settles. */
        landingKeys:
            state.phase === "settling" ? state.landingKeys : NO_LANDING_KEYS,
        targetPlacement:
            state.phase === "dragging" ? state.target.placement : null,
        sortHintCellKey: getSortHintCellKey(model, state),
        /** The lane the card was picked up in, where collapsed strips drop it. */
        originLaneId: state.phase === "dragging" ? state.originLaneId : null,
        announceLockedCard,
        registerActivator,
        stepKeyboard,
        settle,
        isSettling,
        releaseOrphanedDrag,
        handlers: {
            onDragStart,
            onDragMove: onDragHover,
            onDragOver: onDragHover,
            onDragEnd,
            onDragCancel,
        },
    };
};
